import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { AppRolloutEntry } from '../types/admin';

const APP_ROLLOUT_COLLECTION = 'appRollout';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CacheEntry {
  data: AppRolloutEntry[];
  timestamp: number;
}

// Cache for app rollout entries
const appRolloutCache = new Map<string, CacheEntry>();

// Convert Firestore data to AppRolloutEntry
const convertToAppRolloutEntry = (doc: any): AppRolloutEntry => {
  const data = doc.data();
  return {
    id: doc.id,
    appId: data.appId,
    date: data.date?.toDate() || new Date(),
    phase: data.phase || '',
    users: data.users || [],
    note: data.note || '',
    createdAt: data.createdAt?.toDate() || new Date()
  };
};

// Get all app rollout entries
export const getAllAppRolloutEntries = async (): Promise<AppRolloutEntry[]> => {
  const cacheKey = 'all';
  const now = Date.now();
  const cached = appRolloutCache.get(cacheKey);

  // Return cached data if valid
  if (cached && now - cached.timestamp < CACHE_TTL) {
    
    return cached.data;
  }

  
  const q = query(
    collection(db, APP_ROLLOUT_COLLECTION),
    orderBy('date', 'desc')
  );
  
  const querySnapshot = await getDocs(q);
  const entries = querySnapshot.docs.map(convertToAppRolloutEntry);

  // Cache the result
  appRolloutCache.set(cacheKey, { data: entries, timestamp: now });
  return entries;
};

// Get app rollout entries for a specific app
export const getAppRolloutEntriesByApp = async (appId: string): Promise<AppRolloutEntry[]> => {
  const cacheKey = `app_${appId}`;
  const now = Date.now();
  const cached = appRolloutCache.get(cacheKey);

  // Return cached data if valid
  if (cached && now - cached.timestamp < CACHE_TTL) {
    
    return cached.data;
  }

  
  const q = query(
    collection(db, APP_ROLLOUT_COLLECTION),
    where('appId', '==', appId),
    orderBy('date', 'desc')
  );
  
  const querySnapshot = await getDocs(q);
  const entries = querySnapshot.docs.map(convertToAppRolloutEntry);

  // Cache the result
  appRolloutCache.set(cacheKey, { data: entries, timestamp: now });
  return entries;
};

// Add a new app rollout entry
export const addAppRolloutEntry = async (entry: Omit<AppRolloutEntry, 'id' | 'createdAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, APP_ROLLOUT_COLLECTION), {
    ...entry,
    date: Timestamp.fromDate(entry.date),
    createdAt: serverTimestamp()
  });

  // Clear cache
  clearAppRolloutCache();
  
  return docRef.id;
};

// Update an existing app rollout entry
export const updateAppRolloutEntry = async (id: string, entry: Partial<Omit<AppRolloutEntry, 'id' | 'createdAt'>>): Promise<void> => {
  const docRef = doc(db, APP_ROLLOUT_COLLECTION, id);
  
  // Convert date to Timestamp if provided
  const updateData: any = { ...entry };
  if (entry.date) {
    updateData.date = Timestamp.fromDate(entry.date);
  }
  
  await updateDoc(docRef, updateData);

  // Clear cache
  clearAppRolloutCache();
};

// Delete an app rollout entry
export const deleteAppRolloutEntry = async (id: string): Promise<void> => {
  const docRef = doc(db, APP_ROLLOUT_COLLECTION, id);
  await deleteDoc(docRef);

  // Clear cache
  clearAppRolloutCache();
};

// Get a single app rollout entry by ID
export const getAppRolloutEntryById = async (id: string): Promise<AppRolloutEntry | null> => {
  const docRef = doc(db, APP_ROLLOUT_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return convertToAppRolloutEntry(docSnap);
  }
  
  return null;
};

// Clear all app rollout caches
export const clearAppRolloutCache = () => {
  appRolloutCache.clear();
};
